import axios from 'axios';

// Use relative URL so it works on any domain (preview, production, custom domain)
const API_URL = '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('rpas_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const headers = getAuthHeaders();
  config.headers = { ...config.headers, ...headers };
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only force logout if NOT on login page and NOT a login request
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      if (!isLoginRequest && window.location.pathname !== '/login') {
        localStorage.removeItem('rpas_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
};

// Projects
export const projectsAPI = {
  list: () => api.get('/projects'),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

// Drones
export const dronesAPI = {
  list: (projectId) => api.get('/drones', { params: { project_id: projectId } }),
  create: (data) => api.post('/drones', data),
  update: (id, data) => api.put(`/drones/${id}`, data),
  delete: (id) => api.delete(`/drones/${id}`),
};

// Users
export const usersAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Shared Links
export const linksAPI = {
  list: (projectId) => api.get('/shared-links', { params: { project_id: projectId } }),
  create: (data) => api.post('/shared-links', data),
  delete: (id) => api.delete(`/shared-links/${id}`),
  getStream: (token) => axios.get(`${API_URL}/stream/${token}`),
};

// Messages
export const messagesAPI = {
  list: (projectId, limit = 100) => api.get(`/messages/${projectId}`, { params: { limit } }),
  create: (data) => api.post('/messages', data),
};

// File Upload
export const uploadAPI = {
  upload: (formData) => api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Time Entries
export const timeAPI = {
  list: (projectId, userId) => api.get('/time-entries', { params: { project_id: projectId, user_id: userId } }),
  create: (data) => api.post('/time-entries', data),
  status: (userId) => api.get(`/time-entries/status/${userId}`),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
  metrics: (days) => api.get('/dashboard/metrics', { params: days ? { days } : {} }),
  defaultIframes: () => api.get('/default-iframes'),
};

// Dashboard Streams (Super Admin CRUD)
export const streamsAPI = {
  list: () => api.get('/default-iframes'),
  create: (data) => api.post('/dashboard-streams', data),
  delete: (id) => api.delete(`/dashboard-streams/${id}`),
};

// Project Videos
export const videosAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/videos`),
  upload: (projectId, formData, onProgress) => api.post(`/projects/${projectId}/videos/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
    timeout: 600000,
  }),
  delete: (projectId, videoId) => api.delete(`/projects/${projectId}/videos/${videoId}`),
  streamUrl: (videoId, token) => `/api/videos/${videoId}/stream?auth=${token}`,
  sharedInfo: (shareToken) => axios.get(`${API_URL}/shared-video/${shareToken}`),
  sharedStreamUrl: (shareToken) => `/api/shared-video/${shareToken}/stream`,
};

export const mediaAPI = {
  list: (projectId, folderId) => api.get(`/projects/${projectId}/media`, { params: { folder_id: folderId || null } }),
  breadcrumb: (projectId, folderId) => api.get(`/projects/${projectId}/media/breadcrumb/${folderId}`),
  storageStats: (projectId) => api.get(`/projects/${projectId}/media/storage-stats`),
  createFolder: (projectId, data) => api.post(`/projects/${projectId}/media/folder`, data),
  initUpload: (projectId, data) => api.post(`/projects/${projectId}/media/init-upload`, data),
  uploadChunk: (projectId, uploadId, chunkIndex, formData, onProgress) =>
    api.post(`/projects/${projectId}/media/upload-chunk?upload_id=${uploadId}&chunk_index=${chunkIndex}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
      timeout: 300000,
    }),
  completeUpload: (projectId, data) => api.post(`/projects/${projectId}/media/complete-upload`, data),
  uploadStatus: (projectId, uploadId) => api.get(`/projects/${projectId}/media/upload-status/${uploadId}`),
  abortUpload: (projectId, uploadId) => api.delete(`/projects/${projectId}/media/upload-abort/${uploadId}`),
  prewarm: (mediaId) => api.post(`/media/${mediaId}/prewarm`),
  quickUpload: (projectId, formData, folderId, onProgress) =>
    api.post(`/projects/${projectId}/media/quick-upload${folderId ? `?folder_id=${folderId}` : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
      timeout: 600000,
    }),
  delete: (projectId, mediaId) => api.delete(`/projects/${projectId}/media/${mediaId}`),
  share: (mediaId) => api.post(`/media/${mediaId}/share`),
  streamUrl: (mediaId, token) => `/api/media/${mediaId}/stream?auth=${token}`,
  thumbnailUrl: (mediaId, token) => `/api/media/${mediaId}/thumbnail?auth=${token}`,
  sharedInfo: (shareToken) => axios.get(`${API_URL}/shared-media/${shareToken}`),
  sharedStreamUrl: (shareToken) => `/api/shared-media/${shareToken}/stream`,
};

// Drone Control
export const droneAPI = {
  status: () => api.get('/drone/status'),
  command: (command, params) => api.post('/drone/command', { command, params }),
  commandLog: () => api.get('/drone/command/log'),
};

export default api;
